export type StorageCodec<T> = {
  decode(value: unknown): T
  encode?(value: T): unknown
}

export type StorageSource = Storage | (() => Storage | undefined)

export const localStorageSource = () => (typeof localStorage === "undefined" ? undefined : localStorage)

function resolveStorage(source: StorageSource) {
  return typeof source === "function" ? source() : source
}

export function createJSONStorage<T>(key: string, codec: StorageCodec<T>, source: StorageSource = localStorageSource) {
  return {
    read(fallback: T): T {
      try {
        const raw = resolveStorage(source)?.getItem(key)
        return raw == null ? fallback : codec.decode(JSON.parse(raw))
      } catch {
        return fallback
      }
    },
    write(value: T, options?: { removeWhen?: (value: T) => boolean }) {
      try {
        const storage = resolveStorage(source)
        if (!storage) return
        if (options?.removeWhen?.(value)) {
          storage.removeItem(key)
          return
        }
        storage.setItem(key, JSON.stringify(codec.encode?.(value) ?? value))
      } catch {
        // Preferences and caches are best effort; server-backed state must not use this adapter.
      }
    },
    remove() {
      try {
        resolveStorage(source)?.removeItem(key)
      } catch {
        // Best effort.
      }
    },
  }
}

export function createStringStorage(key: string, source: StorageSource = localStorageSource) {
  return {
    read(fallback = "") {
      try {
        return resolveStorage(source)?.getItem(key) ?? fallback
      } catch {
        return fallback
      }
    },
    write(value: string) {
      try {
        const storage = resolveStorage(source)
        if (!storage) return
        if (value) storage.setItem(key, value)
        else storage.removeItem(key)
      } catch {
        // Best effort.
      }
    },
    remove() {
      try {
        resolveStorage(source)?.removeItem(key)
      } catch {
        // Best effort.
      }
    },
  }
}

export const sessionStorageSource = () => (typeof sessionStorage === "undefined" ? undefined : sessionStorage)

export const indexedDBSource = () => (typeof indexedDB === "undefined" ? undefined : indexedDB)

function resilientStorage(source: StorageSource): Storage {
  return {
    get length() {
      try {
        return resolveStorage(source)?.length ?? 0
      } catch {
        return 0
      }
    },
    clear() {
      try {
        resolveStorage(source)?.clear()
      } catch {
        // Best effort.
      }
    },
    getItem(key: string) {
      try {
        return resolveStorage(source)?.getItem(key) ?? null
      } catch {
        return null
      }
    },
    key(index: number) {
      try {
        return resolveStorage(source)?.key(index) ?? null
      } catch {
        return null
      }
    },
    removeItem(key: string) {
      try {
        resolveStorage(source)?.removeItem(key)
      } catch {
        // Best effort.
      }
    },
    setItem(key: string, value: string) {
      try {
        resolveStorage(source)?.setItem(key, value)
      } catch {
        // Preferences and disposable caches are best effort.
      }
    },
  }
}

export const localValues = resilientStorage(localStorageSource)
export const sessionValues = resilientStorage(sessionStorageSource)
